import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Wrench, Upload, CheckCircle, Loader, AlertCircle, Camera } from 'lucide-react';
interface MaintenanceRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}
type Step = 'details' | 'processing' | 'success';
type Priority = 'low' | 'medium' | 'high' | 'urgent';
type Category = 'plumbing' | 'electrical' | 'appliance' | 'structural' | 'hvac' | 'other';
export function MaintenanceRequestModal({
  isOpen,
  onClose,
  onSuccess
}: MaintenanceRequestModalProps) {
  const [step, setStep] = useState<Step>('details');
  const [formData, setFormData] = useState({
    category: 'plumbing' as Category,
    priority: 'medium' as Priority,
    title: '',
    description: '',
    location: '',
    accessInstructions: ''
  });
  const handleSubmit = () => {
    setStep('processing');
    setTimeout(() => {
      setStep('success');
      setTimeout(() => {
        onSuccess();
        onClose();
        resetModal();
      }, 2000);
    }, 2000);
  };
  const resetModal = () => {
    setStep('details');
    setFormData({
      category: 'plumbing',
      priority: 'medium',
      title: '',
      description: '',
      location: '',
      accessInstructions: ''
    });
  };
  const handleClose = () => {
    if (step !== 'processing') {
      onClose();
      setTimeout(resetModal, 300);
    }
  };
  const priorityColors = {
    low: 'border-gray-300 bg-gray-50 text-gray-700',
    medium: 'border-yellow-300 bg-yellow-50 text-yellow-700',
    high: 'border-orange-300 bg-orange-50 text-orange-700',
    urgent: 'border-red-300 bg-red-50 text-red-700'
  };
  return <AnimatePresence data-id="element-1520">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={handleClose} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" data-id="element-1521" />

          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-1522">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-id="element-1523">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10" data-id="element-1524">
                <h2 className="text-2xl font-bold text-gray-900" data-id="element-1525">
                  {step === 'success' ? 'Request Submitted!' : 'Submit Maintenance Request'}
                </h2>
                {step !== 'processing' && <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1526">
                    <X className="h-5 w-5 text-gray-500" data-id="element-1527" />
                  </button>}
              </div>

              <div className="p-6" data-id="element-1528">
                {step === 'details' && <div className="space-y-6" data-id="element-1529">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1530">
                      <div data-id="element-1531">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1532">
                          Category *
                        </label>
                        <select value={formData.category} onChange={e => setFormData({
                    ...formData,
                    category: e.target.value as Category
                  })} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1533">
                          <option value="plumbing" data-id="element-1534">Plumbing</option>
                          <option value="electrical" data-id="element-1535">Electrical</option>
                          <option value="appliance" data-id="element-1536">Appliance</option>
                          <option value="structural" data-id="element-1537">Structural</option>
                          <option value="hvac" data-id="element-1538">HVAC</option>
                          <option value="other" data-id="element-1539">Other</option>
                        </select>
                      </div>

                      <div data-id="element-1540">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1541">
                          Priority *
                        </label>
                        <div className="grid grid-cols-2 gap-2" data-id="element-1542">
                          {(['low', 'medium', 'high', 'urgent'] as Priority[]).map(priority => <button key={priority} onClick={() => setFormData({
                      ...formData,
                      priority
                    })} className={`px-4 py-2 rounded-lg border-2 font-medium capitalize transition-all ${formData.priority === priority ? priorityColors[priority] : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`} data-id="element-1543">
                              {priority}
                            </button>)}
                        </div>
                      </div>
                    </div>

                    <div data-id="element-1544">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1545">
                        Issue Title *
                      </label>
                      <input type="text" value={formData.title} onChange={e => setFormData({
                  ...formData,
                  title: e.target.value
                })} placeholder="e.g., Leaking kitchen faucet" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1546" />
                    </div>

                    <div data-id="element-1547">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1548">
                        Detailed Description *
                      </label>
                      <textarea value={formData.description} onChange={e => setFormData({
                  ...formData,
                  description: e.target.value
                })} placeholder="Please describe the issue in detail..." rows={4} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1549" />
                    </div>

                    <div data-id="element-1550">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1551">
                        Location in Property *
                      </label>
                      <input type="text" value={formData.location} onChange={e => setFormData({
                  ...formData,
                  location: e.target.value
                })} placeholder="e.g., Master bathroom, Kitchen sink" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1552" />
                    </div>

                    <div data-id="element-1553">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1554">
                        Access Instructions
                      </label>
                      <textarea value={formData.accessInstructions} onChange={e => setFormData({
                  ...formData,
                  accessInstructions: e.target.value
                })} placeholder="Any special instructions for accessing the area? (Optional)" rows={2} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1555" />
                    </div>

                    <div data-id="element-1556">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1557">
                        Photos (Optional)
                      </label>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-500 transition-colors cursor-pointer" data-id="element-1558">
                        <Camera className="h-12 w-12 text-gray-400 mx-auto mb-4" data-id="element-1559" />
                        <p className="text-sm text-gray-600 mb-2" data-id="element-1560">
                          Click to upload photos of the issue
                        </p>
                        <p className="text-xs text-gray-500" data-id="element-1561">
                          PNG, JPG up to 10MB (Max 5 photos)
                        </p>
                      </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-1562">
                      <div className="flex items-start" data-id="element-1563">
                        <AlertCircle className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" data-id="element-1564" />
                        <div className="text-sm text-blue-800" data-id="element-1565">
                          <p className="font-medium mb-1" data-id="element-1566">Response Time</p>
                          <p data-id="element-1567">
                            {formData.priority === 'urgent' && 'Emergency: Within 2 hours'}
                            {formData.priority === 'high' && 'High Priority: Within 24 hours'}
                            {formData.priority === 'medium' && 'Standard: Within 3 business days'}
                            {formData.priority === 'low' && 'Low Priority: Within 7 business days'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-3" data-id="element-1568">
                      <button onClick={handleClose} className="flex-1 px-6 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-1569">
                        Cancel
                      </button>
                      <button onClick={handleSubmit} disabled={!formData.title || !formData.description || !formData.location} className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center" data-id="element-1570">
                        <Wrench className="h-5 w-5 mr-2" data-id="element-1571" />
                        Submit Request
                      </button>
                    </div>
                  </div>}

                {step === 'processing' && <div className="py-12 text-center" data-id="element-1572">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6" data-id="element-1573">
                      <Loader className="h-8 w-8 text-blue-600 animate-spin" data-id="element-1574" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1575">
                      Submitting Request...
                    </h3>
                    <p className="text-gray-600" data-id="element-1576">
                      Please wait while we process your maintenance request.
                    </p>
                  </div>}

                {step === 'success' && <div className="py-12 text-center" data-id="element-1577">
                    <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                type: 'spring',
                stiffness: 200,
                damping: 15
              }} className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6" data-id="element-1578">
                      <CheckCircle className="h-8 w-8 text-green-600" data-id="element-1579" />
                    </motion.div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1580">
                      Request Submitted!
                    </h3>
                    <p className="text-gray-600 mb-6" data-id="element-1581">
                      Your maintenance request has been submitted successfully.
                    </p>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 max-w-md mx-auto" data-id="element-1582">
                      <p className="text-sm text-green-800" data-id="element-1583">
                        <strong data-id="element-1584">
                          Request ID: #{Math.floor(Math.random() * 10000)}
                        </strong>
                        <br data-id="element-1585" />
                        Expected response:{' '}
                        {formData.priority === 'urgent' ? 'Within 2 hours' : formData.priority === 'high' ? 'Within 24 hours' : formData.priority === 'medium' ? 'Within 3 business days' : 'Within 7 business days'}
                      </p>
                    </div>
                  </div>}
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}